import React from 'react'
import PropTypes from 'prop-types'
import useDispatch from 'modules/component/utils/hooks/useDispatch.js'
import HeaderAvatar from 'components/Header/Header.Avatar'
import HeaderButton from 'components/Header/Header.Button'
import HeaderNotifications from 'components/Header/Header.Notifications'
import { View } from 'components/View'
import {
  canCreateInstancesUserUtil,
  isAdminUserUtil
} from 'models/daos/user/utils'
import { resetEditingAction } from 'modules/editing/actions'
import openModalAction from 'modules/modal/actions/open'
import { MODAL_ID } from 'modules/modal/constants/index.cjs'
import { deleteLegacyReduxAction } from 'modules/redux/actions/legacy/delete'
import { discardLegacyReduxAction } from 'modules/redux/actions/legacy/discard'
import { saveLegacyReduxAction } from 'modules/redux/actions/legacy/save'
import getPathReduxUtil from 'modules/redux/utils/get-path'
import hasChangedReduxUtil from 'modules/redux/utils/has-changed'
import isAppRouteUtil from 'modules/routes/utils/is-app'
import isNewRouteUtil from 'modules/routes/utils/is-new'
import { STYLES_COLOR } from 'modules/styles/constants/index.cjs'
import { removeEndOfStringMatchStringUtil } from 'utils/string'
import pushRouterAction from 'modules/router/actions/push'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import getCurrentUserSelector from 'modules/users/selectors/get-current/index.js'
import getInstanceSectionSelector from 'modules/section/selectors/get-instance/index.js'

export const HeaderUserActions = ({
  location,
  isSmallScreen,
  isEditing,
  route,
  match
}) => {
  const instance = useSelector(state => getInstanceSectionSelector()(state, {
    route,
    match
  }))
  const dispatch = useDispatch()
  const currentUser = useSelector(getCurrentUserSelector())

  const handleAfterDelete = () => {
    dispatch(pushRouterAction(`/${route.section}`))
  }

  const handleClickAvatar = () => {
    dispatch(openModalAction(MODAL_ID.USER_MENU))
  }

  const handleClickCancel = () => {
    dispatch(openModalAction(MODAL_ID.CONFIRM, {
      confirmLabel: 'Discard',
      confirmColor: STYLES_COLOR.RED,
      message: 'You have not finished your changes. Do you want to leave without finishing?',
      onClickConfirm: handleClickDiscard
    }))
  }

  const handleClickConfirmDelete = () => {
    dispatch(deleteLegacyReduxAction({ path: getPathReduxUtil(instance) }))
      .then(handleAfterDelete)
  }

  const handleClickDelete = () => {
    dispatch(openModalAction(MODAL_ID.CONFIRM, {
      message: `Are you sure you want to delete this ${instance.className.toLowerCase()}?`,
      confirmLabel: 'Delete',
      confirmColor: STYLES_COLOR.RED,
      onClickConfirm: handleClickConfirmDelete
    }))
  }

  const handleClickDiscard = () => {
    dispatch(discardLegacyReduxAction(getPathReduxUtil(instance)))
    done()
  }

  const handleClickDone = () => {
    done()
  }

  const handleClickNew = () => {
    dispatch(openModalAction(MODAL_ID.NEW))
  }

  const handleClickSave = () => {
    dispatch(saveLegacyReduxAction({ path: getPathReduxUtil(instance) }))
  }

  const done = () => {
    let pathWithoutEditing = removeEndOfStringMatchStringUtil(location.pathname, '/edit')
    pathWithoutEditing = removeEndOfStringMatchStringUtil(pathWithoutEditing, '/new')

    if (isAppRouteUtil(pathWithoutEditing)) {
      const parts = pathWithoutEditing.split('/')
      pathWithoutEditing = parts.slice(0, parts.length - 1).join('/')
    }

    if (!pathWithoutEditing || pathWithoutEditing === '/pages') {
      pathWithoutEditing = '/'
    }

    dispatch(resetEditingAction())
    dispatch(pushRouterAction(pathWithoutEditing))
  }

  const canCurrentUserCreateInstances = () => {
    return canCreateInstancesUserUtil(currentUser)
  }

  const renderAvatar = () => {
    if (!currentUser || isSmallScreen || isEditing) return null
    const value = currentUser.data.avatarThumbnail || currentUser.data.avatar
    return (
      <HeaderAvatar
        value={value}
        onClick={handleClickAvatar}
      />
    )
  }

  const renderNewButton = () => {
    if (isEditing || !canCurrentUserCreateInstances()) return null
    return (
      <HeaderButton
        label='New'
        onClick={handleClickNew}
      />
    )
  }

  const renderNotifications = () => {
    if (isSmallScreen || !currentUser || isEditing) return null
    return <HeaderNotifications location={location} />
  }

  const renderCancelButton = () => {
    if (!hasChangedReduxUtil(instance)) return null
    return (
      <HeaderButton
        label='Cancel'
        onClick={handleClickCancel}
      />
    )
  }

  const renderDeleteButton = () => {
    if (!isAdminUserUtil(currentUser) || isNewRouteUtil(location.pathname)) return null
    return (
      <HeaderButton
        label='Delete'
        onClick={handleClickDelete}
      />
    )
  }

  const renderDoneButton = () => {
    if (hasChangedReduxUtil(instance)) return null
    return (
      <HeaderButton
        label='Done'
        onClick={handleClickDone}
      />
    )
  }

  const renderSaveButton = () => {
    if (!hasChangedReduxUtil(instance)) return null
    return (
      <HeaderButton
        label='Save'
        onClick={handleClickSave}
      />
    )
  }

  const renderEditingButtons = () => {
    if (!isEditing) return null
    return (
      <View style={{ flexDirection: View.DIRECTION.ROW }}>
        {renderDeleteButton()}
        {renderDoneButton()}
        {renderCancelButton()}
        {renderSaveButton()}
      </View>
    )
  }

  return (
    <>
      {renderEditingButtons()}
      {renderNotifications()}
      {renderNewButton()}
      {renderAvatar()}
    </>
  )
}

HeaderUserActions.propTypes = {
  instance: PropTypes.object,
  location: PropTypes.object.isRequired,
  isSmallScreen: PropTypes.bool,
  isEditing: PropTypes.bool.isRequired,
  route: PropTypes.object.isRequired
}
